import React, { useEffect, useState } from 'react';
import { CalendarIcon, DollarSignIcon, RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon } from 'lucide-react';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  // Payment details
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  // Subscription details
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState('');
  const [trialDays, setTrialDays] = useState('0');
  // Customer selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    name: string;
    email: string;
    cards: Array<{
      id: string;
      last4: string;
      brand: string;
      expiry: string;
    }>;
  } | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  // Subscription mode
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  // Customer email/phone for new subscription
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Notify parent component when subscription mode changes
  useEffect(() => {
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(subscriptionMode);
    }
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Mock customer data
  const customers = [{
    id: 'cust_1',
    name: 'John Doe',
    email: 'john@example.com',
    cards: [{
      id: 'card_1',
      last4: '4242',
      brand: 'Visa',
      expiry: '04/25'
    }, {
      id: 'card_2',
      last4: '5555',
      brand: 'Mastercard',
      expiry: '07/24'
    }]
  }, {
    id: 'cust_2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    cards: [{
      id: 'card_3',
      last4: '1234',
      brand: 'Visa',
      expiry: '09/26'
    }]
  }];
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    setAmount(value);
    onAmountChange(value);
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrency(e.target.value);
    onCurrencyChange(e.target.value);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedCustomer(null);
    setSelectedCard(null);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
  };
  const handleSelectCard = (cardId: string) => {
    setSelectedCard(cardId);
  };
  const handleSubscriptionModeChange = (mode: 'existing' | 'new') => {
    setSubscriptionMode(mode);
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(mode);
    }
  };
  // Filter customers based on search term
  const filteredCustomers = searchTerm ? customers.filter(customer => customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  return <div className="space-y-6" data-id="element-2741">
      {/* Subscription Mode Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2742">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2743">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-2744" />
          Subscription Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2745">
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('existing')} data-id="element-2746">
            <div className="flex items-start space-x-3" data-id="element-2747">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-2748">
                <CreditCardIcon size={20} className="text-blue-600" data-id="element-2749" />
              </div>
              <div data-id="element-2750">
                <h3 className="font-medium text-gray-900" data-id="element-2751">Existing Customer</h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2752">
                  Create a subscription for an existing customer with a saved
                  payment method
                </p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('new')} data-id="element-2753">
            <div className="flex items-start space-x-3" data-id="element-2754">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-2755">
                <LinkIcon size={20} className="text-blue-600" data-id="element-2756" />
              </div>
              <div data-id="element-2757">
                <h3 className="font-medium text-gray-900" data-id="element-2758">
                  New Subscription Link
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2759">
                  Send a link to customer to start a new subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Customer Selection (for existing customers) */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2760">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2761">
            Customer Selection
          </h3>
          <div className="space-y-4" data-id="element-2762">
            <div data-id="element-2763">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2764">
                Search Customer
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2765">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2766">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-2767" />
                </div>
                <input type="text" value={searchTerm} onChange={handleSearchChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name or email" data-id="element-2768" />
              </div>
            </div>
            {searchTerm && filteredCustomers.length > 0 && !selectedCustomer && <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2769">
                  <ul className="divide-y divide-gray-200" data-id="element-2770">
                    {filteredCustomers.map(customer => <li key={customer.id} className="hover:bg-gray-50" data-id="element-2771">
                        <button type="button" onClick={() => handleSelectCustomer(customer)} className="w-full px-4 py-3 text-left flex items-center" data-id="element-2772">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-2773">
                            <UserIcon size={16} data-id="element-2774" />
                          </div>
                          <div data-id="element-2775">
                            <p className="text-sm font-medium text-gray-900" data-id="element-2776">
                              {customer.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2777">
                              {customer.email}
                            </p>
                          </div>
                        </button>
                      </li>)}
                  </ul>
                </div>}
            {selectedCustomer && <div className="bg-gray-50 rounded-md p-4" data-id="element-2778">
                <div className="flex items-center justify-between" data-id="element-2779">
                  <div className="flex items-center" data-id="element-2780">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-2781">
                      <UserIcon size={20} data-id="element-2782" />
                    </div>
                    <div data-id="element-2783">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2784">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2785">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelectedCustomer(null)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2786">
                    Change
                  </button>
                </div>
                <div className="mt-4" data-id="element-2787">
                  <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-2788">
                    Saved Payment Methods
                  </p>
                  <div className="space-y-2" data-id="element-2789">
                    {selectedCustomer.cards.map(card => <div key={card.id} onClick={() => handleSelectCard(card.id)} className={`border rounded-md p-3 cursor-pointer ${selectedCard === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'}`} data-id="element-2790">
                        <div className="flex items-center justify-between" data-id="element-2791">
                          <div className="flex items-center" data-id="element-2792">
                            <div className="mr-3" data-id="element-2793">
                              {card.brand === 'Visa' ? <CreditCardIcon size={20} className="text-blue-700" data-id="element-2794" /> : card.brand === 'Mastercard' ? <CreditCardIcon size={20} className="text-red-600" data-id="element-2795" /> : <CreditCardIcon size={20} className="text-gray-600" data-id="element-2796" />}
                            </div>
                            <div data-id="element-2797">
                              <p className="text-sm font-medium text-gray-900" data-id="element-2798">
                                {card.brand} •••• {card.last4}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2799">
                                Expires {card.expiry}
                              </p>
                            </div>
                          </div>
                          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-2800">
                            {selectedCard === card.id && <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-2801"></div>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      {/* Customer Information (for new subscription) */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2802">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-2803">
            Customer Information
          </h3>
          <div className="space-y-4" data-id="element-2804">
            <div data-id="element-2805">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2806">
                Customer Name (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2807">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2808">
                  <UserIcon size={16} className="text-gray-400" data-id="element-2809" />
                </div>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="John Doe" data-id="element-2810" />
              </div>
            </div>
            <div data-id="element-2811">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2812">
                Customer Email
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2813">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2814">
                  <MailIcon size={16} className="text-gray-400" data-id="element-2815" />
                </div>
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customer@example.com" required data-id="element-2816" />
              </div>
            </div>
            <div data-id="element-2817">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2818">
                Customer Phone (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2819">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2820">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-2821" />
                </div>
                <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (555) 123-4567" data-id="element-2822" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-2823">
                We'll send a subscription link to the customer via email or SMS
              </p>
            </div>
          </div>
        </div>}
      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2824">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2825">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-2826" />
          Subscription Details
        </h3>
        <div className="space-y-4" data-id="element-2827">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2828">
            <div data-id="element-2829">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2830">
                Amount per Billing Period*
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2831">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2832">
                  <DollarSignIcon size={16} className="text-gray-400" data-id="element-2833" />
                </div>
                <input type="text" value={amount} onChange={handleAmountChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-2834" />
              </div>
            </div>
            <div data-id="element-2835">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2836">
                Currency
              </label>
              <select value={currency} onChange={handleCurrencyChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2837">
                <option value="USD" data-id="element-2838">USD - US Dollar</option>
                <option value="EUR" data-id="element-2839">EUR - Euro</option>
                <option value="GBP" data-id="element-2840">GBP - British Pound</option>
                <option value="JPY" data-id="element-2841">JPY - Japanese Yen</option>
                <option value="CAD" data-id="element-2842">CAD - Canadian Dollar</option>
                <option value="AUD" data-id="element-2843">AUD - Australian Dollar</option>
              </select>
            </div>
          </div>
          <div data-id="element-2844">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2845">
              Description*
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Premium Membership" required data-id="element-2846" />
          </div>
          <div data-id="element-2847">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2848">
              Your Reference (Optional)
            </label>
            <input type="text" value={reference} onChange={handleReferenceChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Internal reference" data-id="element-2849" />
          </div>
        </div>
      </div>
      {/* Billing Schedule */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2850">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2851">
          <ClockIcon size={20} className="mr-2 text-blue-600" data-id="element-2852" />
          Billing Schedule
        </h3>
        <div className="space-y-4" data-id="element-2853">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2854">
            <div data-id="element-2855">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2856">
                Billing Interval
              </label>
              <select value={billingInterval} onChange={e => setBillingInterval(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-2857">
                <option value="weekly" data-id="element-2858">Weekly</option>
                <option value="biweekly" data-id="element-2859">Every 2 Weeks</option>
                <option value="monthly" data-id="element-2860">Monthly</option>
                <option value="quarterly" data-id="element-2861">Quarterly</option>
                <option value="biannually" data-id="element-2862">Every 6 Months</option>
                <option value="annually" data-id="element-2863">Annually</option>
              </select>
            </div>
            <div data-id="element-2864">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2865">
                Number of Billing Cycles (Optional)
              </label>
              <input type="number" min="1" value={billingCycles} onChange={e => setBillingCycles(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Leave blank for unlimited" data-id="element-2866" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-2867">
                Leave blank for an ongoing subscription
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2868">
            <div data-id="element-2869">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2870">
                Start Date
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-2871">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2872">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-2873" />
                </div>
                <input type="text" value={startDate} onChange={e => setStartDate(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" data-id="element-2874" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-2875">
                Leave blank to start immediately
              </p>
            </div>
            <div data-id="element-2876">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2877">
                Trial Period (Days)
              </label>
              <input type="number" min="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-2878" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-2879">
                Free trial before first payment
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2880">
        <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-2881">
          Subscription Summary
        </h3>
        <div className="space-y-2" data-id="element-2882">
          <div className="flex justify-between" data-id="element-2883">
            <span className="text-sm text-gray-600" data-id="element-2884">Amount:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2885">
              {amount ? `$${amount} ${currency}` : '-'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2886">
            <span className="text-sm text-gray-600" data-id="element-2887">Billing Interval:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2888">
              {billingInterval === 'weekly' ? 'Weekly' : billingInterval === 'biweekly' ? 'Every 2 Weeks' : billingInterval === 'monthly' ? 'Monthly' : billingInterval === 'quarterly' ? 'Every 3 Months' : billingInterval === 'biannually' ? 'Every 6 Months' : 'Annually'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2889">
            <span className="text-sm text-gray-600" data-id="element-2890">Duration:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2891">
              {billingCycles ? `${billingCycles} billing cycles` : 'Ongoing'}
            </span>
          </div>
          {parseInt(trialDays) > 0 && <div className="flex justify-between" data-id="element-2892">
              <span className="text-sm text-gray-600" data-id="element-2893">Trial Period:</span>
              <span className="text-sm font-medium text-gray-800" data-id="element-2894">
                {trialDays} days
              </span>
            </div>}
          <div className="flex justify-between" data-id="element-2895">
            <span className="text-sm text-gray-600" data-id="element-2896">Start Date:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2897">
              {startDate || 'Immediate'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-2898">
            <span className="text-sm text-gray-600" data-id="element-2899">Customer:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-2900">
              {subscriptionMode === 'existing' ? selectedCustomer ? selectedCustomer.name : 'Not selected' : customerEmail ? customerName || customerEmail : 'Not specified'}
            </span>
          </div>
          {subscriptionMode === 'existing' && selectedCustomer && selectedCard && <div className="flex justify-between" data-id="element-2901">
                <span className="text-sm text-gray-600" data-id="element-2902">Payment Method:</span>
                <span className="text-sm font-medium text-gray-800" data-id="element-2903">
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.brand}{' '}
                  ••••
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.last4}
                </span>
              </div>}
        </div>
      </div>
    </div>;
}